import {
  Box,
  Checkbox,
  Collapse,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import LoadingIcon from "../../../../components/LoadingIcon";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import SvgToIcon from "../../../../icons/SvgToIcon";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";

import salesforce_step_1 from "./images/salesforce_step_1.png";
import salesforce_step_10 from "./images/salesforce_step_10.png";
import salesforce_step_11 from "./images/salesforce_step_11.png";
import salesforce_step_12 from "./images/salesforce_step_12.png";
import salesforce_step_13 from "./images/salesforce_step_13.png";
import salesforce_step_2 from "./images/salesforce_step_2.png";
import salesforce_step_3 from "./images/salesforce_step_3.png";
import salesforce_step_4 from "./images/salesforce_step_4.png";
import salesforce_step_5 from "./images/salesforce_step_5.png";
import salesforce_step_6 from "./images/salesforce_step_6.png";
import salesforce_step_7 from "./images/salesforce_step_7.png";
import salesforce_step_8 from "./images/salesforce_step_8.png";
import salesforce_step_9 from "./images/salesforce_step_9.png";

import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { icons, logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";

const SalesforceIntegration = () => {
  const colors = useColors();
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("salesforce");
  const [authUrl, setAuthUrl] = useState("");
  const [clientID, setClientID] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [callbackURL, setCallbackURL] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { fetchSalesforceCallbackUrl, fetchSalesforceAuthUrl } = useAPI();

  const clientIDLength = 85;
  const clientSecretLength = 64;

  const checkBoxesList = [
    "Require Secret for Web Server Flow",
    "Require Secret for Refresh Token Flow",
    "Enable Authorization Code and Credentials Flow",
    "Enable Token Exchange Flow",
    "Require Secret for Token Exchange Flow",
  ];

  useEffect(() => {
    fetchSalesforceCallbackUrl().then((response) => {
      setCallbackURL(response.data.url);
    });
  }, []);

  useEffect(() => {
    if (
      !isIntegrated &&
      clientID.length === clientIDLength &&
      clientSecret.length === clientSecretLength
    ) {
      setIsButtonLoading(true);
      fetchSalesforceAuthUrl(clientID, clientSecret).then((response) => {
        setAuthUrl(response.data.url);
        setIsButtonLoading(false);
      });
    }
  }, [clientID, clientSecret]);

  if (isIntegratedLoading) {
    return <LoadingIcon />;
  }

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, Breeze will scan your Salesforce CRM for users, datastores and critical business assets."
            : ""
        }
        image={logos.SalesForceLogo}
        name={"Salesforce CRM"}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Fragment>
              <Typography variant={"h4"}>
                Salesforce is already integrated!
              </Typography>
            </Fragment>
          ) : (
            <>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Salesforce as a system Administrator">
                  <Typography variant={"body2"}>
                    Login to your Salesforce account as a system Administrator
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create new app">
                  <Typography variant={"body2"}>
                    On the top right corner, click on settings icon and select{" "}
                    <b>Setup</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: salesforce_step_1,
                        altext: "Create a new app integration",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    In the left menu, select <b>Apps</b> →{" "}
                    <b>External Client Apps</b> → <b>Settings</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: salesforce_step_2,
                        altext: "External Apps Settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Toggle the Connected Apps (Allow creation of connected apps)
                    and then click New connected app
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: salesforce_step_3,
                        altext: "toggle connected apps",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Fill the app details">
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant={"body2"}>
                      Connected App Name: <strong>Breeze Security</strong>
                    </Typography>{" "}
                    <CustomCopyButton
                      copyTitle={"Connected App Name"}
                      copyText={"Breeze Security"}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant={"body2"}>
                      Contact Email:{" "}
                      <strong>support@breeze-security.com</strong>
                    </Typography>{" "}
                    <CustomCopyButton
                      copyTitle={"Contact Email"}
                      copyText={"support@breeze-security.com"}
                    />
                  </Box>
                  <Typography variant={"body2"}>
                    Click on <b>Enable OAuth settings </b>and set the following
                    values:
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant={"body2"}>
                      Callback URL: <strong>{callbackURL} </strong>
                    </Typography>{" "}
                    <CustomCopyButton
                      copyTitle={"Callback URL"}
                      copyText={callbackURL}
                    />
                  </Box>
                  <Typography variant={"body2"}>
                    Selected OAuth Scopes:{" "}
                    <b>
                      Manage user data via APIs (api), Perform requests at any
                      time (refresh_token, offline_access){" "}
                    </b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: salesforce_step_4,
                        altext: "OAuth Scopes",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Mark the following checkboxes:
                  </Typography>
                  <Box
                    sx={{
                      width: "700px",
                      backgroundColor: colors.grey_200,
                      padding: "16px",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color={colors.primary_darker}
                      gutterBottom
                    >
                      The list of checkboxes
                    </Typography>
                    {checkBoxesList.map((role, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <Checkbox />
                        <Typography>{role}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: salesforce_step_5,
                        altext: "checkboxes",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click <b>Save</b>.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Get the customer key and secret">
                  <Typography variant={"body2"}>
                    Click on the <b>Manage Consumer Details</b> button.
                  </Typography>
                  <Typography variant={"body2"}>
                    Copy the <b>Consumer Key</b> and <b>Consumer Secret</b> and
                    paste them below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: salesforce_step_6,
                        altext: "Customer Key",
                      },
                      {
                        file: salesforce_step_7,
                        altext: "Customer secret",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the Customer Key and Customer Secret here">
                  <Grid sx={{ width: "700px" }} spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="filled"
                        name="clientID"
                        label="Customer Key"
                        value={clientID}
                        onChange={(e) => setClientID(e.target.value)}
                        autoComplete="off"
                        disabled={guideMode}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: "10px" }}>
                      <TextField
                        fullWidth
                        variant="filled"
                        name="clientSecret"
                        label="Customer Secret"
                        value={clientSecret}
                        onChange={(e) => setClientSecret(e.target.value)}
                        autoComplete="off"
                        disabled={guideMode}
                      />
                    </Grid>
                  </Grid>
                </GuidelineStep>
                <GuidelineStep title="Optional - If integrating with a user other than the System Administrator">
                  <Box
                    width="fit-content"
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ cursor: "pointer" }}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                  >
                    <SvgToIcon
                      IconLogo={
                        isCollapsed
                          ? icons.chevronDownIcon
                          : icons.chevronRightIcon
                      }
                      width="24px"
                      height="24px"
                    />
                    <Typography variant={"body2"}>View step details</Typography>
                  </Box>
                  <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
                    <Grid container>
                      <Grid item xs={12} sx={{ mt: "12px" }}>
                        <Typography variant={"body2"}>
                          Only if you are integrating with a user{" "}
                          <b>other than the System Administrator</b> - perform
                          this step.
                        </Typography>
                        <Typography variant={"body2"}>
                          Using a System Administrator user - Navigate to{" "}
                          <b>Setup</b> → <b>Users</b> → <b>Permission Sets</b> →{" "}
                          <b>New</b>.
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: salesforce_step_8,
                              altext: "Permission Sets",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ mt: "12px" }}>
                        <Typography variant={"body2"}>Fill in:</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant={"body2"}>Label: </Typography>
                          <Box
                            padding="2px 8px"
                            borderRadius="4px"
                            backgroundColor={colors.grey_300}
                            width="fit-content"
                          >
                            <code style={{ fontSize: "12px" }}>
                              Breeze ReadOnly
                            </code>
                          </Box>
                          <CustomCopyButton
                            copyTitle={"Breeze ReadOnly"}
                            copyText={"Breeze ReadOnly"}
                          />
                        </Box>
                        <Typography variant={"body2"}>
                          User License: None
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ mt: "12px" }}>
                        <Typography variant={"body2"}>
                          Scroll Up and Click <b>Save</b>.
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: salesforce_step_9,
                              altext: "Fill in",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                        <br />
                        <Typography variant={"body2"}>
                          Scroll to <b>System</b> → click{" "}
                          <b>System Permissions</b>.
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: salesforce_step_10,
                              altext: "System permissions",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                        <br />
                        <Typography variant={"body2"} mb={1}>
                          Click <b>Edit</b> and check these checkboxes on the
                          bottom of the page:
                        </Typography>
                        {[
                          "View Setup and Configuration",
                          "View Roles and Role Hierarchy",
                          "Manage Connected Apps",
                          "View all External Client Apps",
                          "View All Data",
                        ].map((text) => (
                          <Box
                            key={text}
                            sx={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <Checkbox sx={{ width: "16px", height: "16px" }} />
                            <Typography variant={"body2"}>{text}</Typography>
                          </Box>
                        ))}
                        <Typography variant={"body2"}>
                          Scroll Up and Click <b>Save</b>.
                        </Typography>
                      </Grid>
                      <Grid item mt="12px">
                        <Typography variant={"body2"} mb={1}>
                          <b>
                            Add the Permission Set to the user performing the
                            integration:
                          </b>
                        </Typography>
                        <Typography variant={"body2"}>
                          Navigate to <b>Setup</b> → <b>Users</b> → <b>Users</b>
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: salesforce_step_11,
                              altext: "users",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                        <br />
                        <Typography variant={"body2"}>
                          Click the user that will be used for performing the
                          integration (don't click edit).
                        </Typography>
                        <Typography variant={"body2"}>
                          Scroll down and click <b>Edit Assignments</b> next to
                          the <b>Permission Set Assignments</b> section
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: salesforce_step_12,
                              altext: "edit assignments",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                        <br />
                        <Typography variant={"body2"}>
                          Select the Permission Set created previously, add it
                          as <b>Enabled Permission Set</b> and then click{" "}
                          <b>Save</b>
                        </Typography>
                        <ScreenshotAccordion
                          images={[
                            {
                              file: salesforce_step_13,
                              altext: "add permission set",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                      </Grid>
                    </Grid>
                  </Collapse>
                </GuidelineStep>
                <GuidelineStep title="Authorize Breeze in Salesforce">
                  <Typography variant={"body2"}>
                    Please allow at least <b>10 minutes</b> after creating the
                    app before clicking this button.
                  </Typography>
                  <ContainedButton
                    color="primary"
                    size="small"
                    disabled={guideMode}
                    loading={isButtonLoading}
                    onClick={() => window.open(authUrl, "_blank")}
                  >
                    Authenticate with Salesforce
                  </ContainedButton>
                </GuidelineStep>
              </GuidelineStepByStep>
            </>
          )
        }
      />
    </Box>
  );
};

export default SalesforceIntegration;
